/**
 * Instrument Detail Header
 * 
 * Shows asset name, class, registration, source, and KPIs
 * Includes scope toggle (Asset vs Position)
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ASSET_CLASS_ICONS } from '@/constants/investmentIcons';
import { ProvenanceBadge } from '@/components/assets/ProvenanceBadge';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';
import type { InstrumentScope } from '@/hooks/useInstrumentDetail';
import { CurrencyAmount } from '@/components/ui/CurrencyAmount';
import { Edit } from 'lucide-react';
import { formatAssetClass } from '@/utils/assetClassDisplay';

interface InstrumentDetailHeaderProps {
  asset: UnifiedAsset;
  scope: InstrumentScope;
  showScopeToggle: boolean;
  onScopeToggle: () => void;
  onEdit?: (asset: UnifiedAsset) => void;
  position?: any;
}

export function InstrumentDetailHeader({
  asset,
  scope,
  showScopeToggle,
  onScopeToggle,
  onEdit,
  position,
}: InstrumentDetailHeaderProps) {
  const displayType = scope === 'position' && position?.asset_type ? position.asset_type : asset.asset_type;
  const AssetIcon = ASSET_CLASS_ICONS[displayType as keyof typeof ASSET_CLASS_ICONS] || ASSET_CLASS_ICONS.cash;
  const kpiValue = scope === 'position' ? (position?.market_value as number | undefined) : (asset.market_value as number | undefined);
  const kpiColorClass = typeof kpiValue === 'number'
    ? (kpiValue > 0 ? 'text-green-600' : kpiValue < 0 ? 'text-destructive' : '')
    : '';

  return (
    <div className="space-y-5 pb-5 border-b">
      {/* Asset name + symbol + actions */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <AssetIcon className="h-6 w-6 text-muted-foreground" />
          <div>
            <h2 className="text-xl font-semibold">{scope === 'position' && position?.asset_name ? position.asset_name : asset.asset_name}</h2>
            {(scope === 'position' ? position?.symbol : asset.symbol) && (
              <p className="text-sm text-muted-foreground">{scope === 'position' ? position?.symbol : asset.symbol}</p>
            )}
          </div>
        </div>

        {/* Edit button moved to link bar under header */}
      </div>

      {/* Badges: Class, Registration, Source */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="secondary" className="capitalize">
          {formatAssetClass(asset.asset_type)}
        </Badge>
        {(asset.registered === 'registered' || (asset as any).registered === true) && (
          <Badge variant="outline">Registered</Badge>
        )}
        {asset.source_type && (
          <div className="flex items-center gap-1">
            <ProvenanceBadge source={asset.source_type} />
            <span className="text-xs text-muted-foreground">
              {scope === 'position' ? 'Holding' : (asset.source_type === 'manual' ? 'Manual' : asset.source_type === 'connected' ? 'Holding' : 'Account')}
            </span>
          </div>
        )}
      </div>

      

      {/* KPIs (As-of moved to Valuation tab) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 text-sm">
        <div>
          <p className="text-muted-foreground">{scope === 'position' ? 'Market Value' : (asset.source_type === 'account' ? 'Current Balance' : 'Market Value')}</p>
          <p className={`font-semibold ${kpiColorClass}`}>
            <CurrencyAmount value={scope === 'position' ? position?.market_value : asset.market_value} currency={scope === 'position' ? (position?.currency || asset.currency) : asset.currency} showCurrency={true} />
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">Quantity</p>
          <p className="font-semibold">{scope === 'position' ? (position?.quantity ?? '—') : (asset.quantity || '—')}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Pricing Method</p>
          <p className="font-semibold capitalize">
            {(() => {
              if (scope === 'position') {
                return (position?.pricing_method?.replace('_', ' ') || 'Market');
              }
              const src = asset.source_type;
              const pm = asset.pricing_method;
              if (pm) return pm.replace('_', ' ');
              // Fallback: for connected account assets, default to Market
              if (src === 'account' || src === 'connected') return 'Market';
              return '—';
            })()}
          </p>
        </div>
      </div>

    </div>
  );
}
